import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { EnumContactPreference } from '@/app/Alert/types';
import { ContactFormValues, contactFormSchema } from '../siteSchema';
import {
  useAddSiteContact,
  useSiteContactsSearch,
  useUpdateSiteContact,
} from '../hooks/useSiteContacts';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PhoneInput, getCountryCodeByName } from '@/components/ui/phone-input';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { ProjectContactDto } from '../siteApi';
import { Search } from 'lucide-react';

interface AddContactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  customerId: string;
  editingContact?: ProjectContactDto | null;
  siteCountry?: string;
}

export const AddContactDialog: React.FC<AddContactDialogProps> = ({
  open,
  onOpenChange,
  projectId,
  customerId,
  editingContact,
  siteCountry,
}) => {
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const { data: searchResults } = useSiteContactsSearch(search);
  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: '',
      lastName: '',
      email: '',
      role: '',
      phone: '',
      companyName: '',
      order: 0,
    },
  });

  useEffect(() => {
    if (editingContact) {
      form.reset({
        name: editingContact.name || '',
        lastName: editingContact.lastName || '',
        email: editingContact.email || '',
        phone: editingContact.phone || '',
        role: editingContact.role || '',
        companyName: editingContact.companyName || '',
        contactPreference: editingContact.contactPreference,
        order: editingContact.order || 0,
      });
    } else {
      form.reset({
        name: '',
        lastName: '',
        email: '',
        phone: '',
        role: '',
        companyName: '',
        order: 0,
      });
    }
  }, [editingContact, form]);

  const addContact = useAddSiteContact(customerId, projectId);
  const updateContact = useUpdateSiteContact(customerId, projectId, editingContact?.id || '');

  const onSubmit = async (data: ContactFormValues) => {
    try {
      if (editingContact && updateContact) {
        await updateContact.mutateAsync({
          projectId,
          id: editingContact.id || '',
          name: data.name || '',
          lastName: data.lastName || '',
          email: data.email || '',
          phone: data.phone || '',
          role: data.role || '',
          companyName: data.companyName || '',
          contactPreference: data.contactPreference || EnumContactPreference.None,
          order: data.order || 0,
        });
      } else {
        await addContact.mutateAsync(data);
      }

      toast({
        title: editingContact ? 'Contact Updated' : 'Contact Added',
        description: editingContact
          ? 'The contact has been updated successfully.'
          : 'The contact has been added successfully.',
      });
      form.reset();
      onOpenChange(false);
    } catch {
      toast({
        title: 'Error',
        description: `Failed to ${editingContact ? 'update' : 'add'} contact. Please try again.`,
        variant: 'destructive',
      });
    }
  };

  const isSubmitting = editingContact ? updateContact?.isPending : addContact.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[840px]">
        <DialogHeader>
          <DialogTitle>{editingContact ? 'Edit Contact' : 'Add Contact'}</DialogTitle>
        </DialogHeader>
        {!editingContact && (
          <div className="flex items-center space-x-2 p-2">
            <Popover modal={false}>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" className="w-full justify-start">
                  <Search />
                  Add an existing contact..
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command>
                  <CommandInput
                    placeholder="Search by name, email, or phone..."
                    onValueChange={setSearch}
                  />
                  <CommandList>
                    <CommandEmpty>No contacts found.</CommandEmpty>
                    <CommandGroup>
                      {searchResults?.map((contact) => {
                        const contactDetails = [contact.email, contact.phone].filter(Boolean);
                        return (
                          <CommandItem
                            key={contact.id}
                            onSelect={() => {
                              form.reset({
                                name: contact.firstName || '',
                                lastName: contact.lastName || '',
                                email: contact.email || '',
                                phone: contact.phone || '',
                                role: contact.role || '',
                                companyName: contact.company || '',
                                order: 0,
                              });
                            }}
                          >
                            <div className="flex flex-col gap-1">
                              <span>
                                {contact.firstName} {contact.lastName}
                              </span>
                              {contactDetails.length > 0 ? (
                                <span className="text-sm text-muted-foreground">
                                  {contactDetails.join(' • ')}
                                </span>
                              ) : (
                                <span className="text-sm text-muted-foreground">No contact info</span>
                              )}
                            </div>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        )}
        <div className="p-4">
          <Form {...form}>
            <form
              onSubmit={(e) => {
                e.stopPropagation();
                form.handleSubmit(onSubmit)(e);
              }}
              className="space-y-4"
            >
              <fieldset className="relative rounded-lg border border-white/10 p-6">
                {!editingContact && (
                  <legend className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 bg-background px-4 text-xs font-black uppercase tracking-[0.25em] text-muted-foreground">
                    OR
                  </legend>
                )}
                {!editingContact &&<h2 className="mb-4 text-lg font-semibold">Create a new contact</h2>}
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                  {/* User Details Column */}
                  <div className="space-y-4 sm:col-span-2">
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      User Details
                    </h3>
                    <div className="space-y-4">
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>First Name</FormLabel>
                            <FormControl>
                              <Input placeholder="John" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="lastName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Last Name</FormLabel>
                            <FormControl>
                              <Input placeholder="Doe" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="role"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Role</FormLabel>
                            <FormControl>
                              <Input placeholder="Manager" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="companyName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Company</FormLabel>
                            <FormControl>
                              <Input placeholder="Company Name" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  {/* Contact Information Column */}
                  <div className="space-y-4">
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Contact Details
                    </h3>
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email (optional)</FormLabel>
                          <FormControl>
                            <Input type="email" placeholder="john@example.com" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Phone</FormLabel>
                          <FormControl>
                            <PhoneInput
                              value={field.value}
                              onChange={field.onChange}
                              defaultCountryCode={getCountryCodeByName(siteCountry)}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="contactPreference"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Contact Preference</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select contact preference" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {Object.values(EnumContactPreference).map((preference) => (
                                <SelectItem key={preference} value={preference}>
                                  {preference === EnumContactPreference.CallOnly
                                    ? 'Call Only'
                                    : preference === EnumContactPreference.SmsOnly
                                      ? 'SMS Only'
                                      : preference === EnumContactPreference.SmsThenCall
                                        ? 'SMS Then Call'
                                        : preference === EnumContactPreference.None
                                          ? 'None'
                                          : preference}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </fieldset>

              <DialogFooter className="m-0 flex flex-row justify-between border-t p-0 pt-4">
                <Button type="submit" disabled={isSubmitting} className="flex-1">
                  {isSubmitting
                    ? editingContact
                      ? 'Updating...'
                      : 'Adding...'
                    : editingContact
                      ? 'Update Contact'
                      : 'Add Contact'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
};
